import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="checkout-timer"
export default class extends Controller {
  static targets = ["countdown", "extendButton"]
  static values = { 
    expiresAt: String,
    intentId: String,
    checkUrl: String,
    extendUrl: String
  }

  connect() {
    this.startCountdown()
    this.setupActivityMonitoring()
    this.checkInterval = setInterval(() => this.checkIntentStatus(), 30000) // Check every 30 seconds
  }

  disconnect() {
    if (this.countdownInterval) clearInterval(this.countdownInterval)
    if (this.checkInterval) clearInterval(this.checkInterval)
    document.removeEventListener('mousemove', this.activityHandler)
    document.removeEventListener('keypress', this.activityHandler)
  }

  startCountdown() {
    const expiresAt = new Date(this.expiresAtValue)
    
    this.countdownInterval = setInterval(() => {
      const now = new Date()
      const timeLeft = expiresAt - now
      
      if (timeLeft <= 0) {
        clearInterval(this.countdownInterval)
        this.countdownTarget.innerHTML = '<span class="text-red-600">Time expired!</span>'
        this.showExpiredMessage()
        return
      }
      
      // Format the time remaining
      const minutes = Math.floor(timeLeft / 60000)
      const seconds = Math.floor((timeLeft % 60000) / 1000)
      
      // Update the countdown display
      this.countdownTarget.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`
      
      // Show warning when less than 2 minutes remain
      if (timeLeft < 120000) {
        this.countdownTarget.classList.add('text-red-600', 'font-bold')
        
        // Show extend button if it exists
        if (this.hasExtendButtonTarget) {
          this.extendButtonTarget.classList.remove('hidden')
        }
      }
    }, 1000)
  }

  setupActivityMonitoring() {
    // Create a debounced activity handler
    let timeout
    this.activityHandler = () => {
      clearTimeout(timeout)
      timeout = setTimeout(() => this.extendIntent(), 5000) // Extend after 5 seconds of inactivity
    }
    
    // Monitor user activity
    document.addEventListener('mousemove', this.activityHandler)
    document.addEventListener('keypress', this.activityHandler)
  }

  async checkIntentStatus() {
    try {
      const response = await fetch(`${this.checkUrlValue}?intent_id=${this.intentIdValue}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      if (!response.ok) throw new Error('Failed to check intent status')
      
      const data = await response.json()
      
      if (data.status === 'expired') {
        this.showExpiredMessage()
        clearInterval(this.checkInterval)
        clearInterval(this.countdownInterval)
      } else if (data.expires_at) {
        // Update the expiry time if it changed
        this.expiresAtValue = data.expires_at
      }
    } catch (error) {
      console.error('Error checking intent status:', error)
    }
  }

  async extendIntent() {
    if (!this.extendUrlValue) return
    
    try {
      const response = await fetch(this.extendUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          intent_id: this.intentIdValue
        })
      })
      
      if (!response.ok) throw new Error('Failed to extend intent')
      
      const data = await response.json()
      
      if (data.expires_at) {
        this.expiresAtValue = data.expires_at
        
        // Reset the countdown styling
        this.countdownTarget.classList.remove('text-red-600', 'font-bold')
        
        // Hide extend button if it exists
        if (this.hasExtendButtonTarget) {
          this.extendButtonTarget.classList.add('hidden')
        }
      }
    } catch (error) {
      console.error('Error extending intent:', error)
    }
  }

  showExpiredMessage() {
    // Create a modal or notification to inform the user
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50'
    modal.innerHTML = `
      <div class="bg-white dark:bg-zinc-800 rounded-xl shadow-lg p-6 max-w-md mx-auto">
        <h3 class="text-xl font-bold text-red-600 mb-4">Reservation Time Expired</h3>
        <p class="mb-4">Your reservation hold has expired. The rental may no longer be available for the selected dates.</p>
        <p class="mb-6">Would you like to check availability again?</p>
        <div class="flex justify-end space-x-4">
          <button id="check-again" class="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">
            Check Availability
          </button>
          <button id="close-modal" class="px-4 py-2 bg-zinc-200 dark:bg-zinc-700 rounded hover:bg-zinc-300 dark:hover:bg-zinc-600">
            Close
          </button>
        </div>
      </div>
    `
    
    document.body.appendChild(modal)
    
    // Add event listeners
    modal.querySelector('#check-again').addEventListener('click', () => {
      window.location.reload()
    })
    
    modal.querySelector('#close-modal').addEventListener('click', () => {
      modal.remove()
    })
  }

  extendTime(event) {
    event.preventDefault()
    this.extendIntent()
  }
}
